import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import {
  getUserPublicProfile,
  getUserPublicCapsules,
  getUserPublicResponses,
  getFollowCounts,
  signOut
} from '../lib/supabase'
import CapsuleCard from '../components/CapsuleCard'
import FollowButton from '../components/FollowButton'
import DirectMessagesButton from '../components/DirectMessagesButton'
import NotificationButton from '../components/NotificationButton'
import './Feed.css'
import './UserProfile.css'

export default function UserProfile() {
  const { username } = useParams()
  const { user, profile: myProfile } = useAuth()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef(null)

  const [profileData, setProfileData] = useState(null)
  const [capsules, setCapsules] = useState([])
  const [responses, setResponses] = useState([])
  const [followCounts, setFollowCounts] = useState({ followers: 0, following: 0 })
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [activeTab, setActiveTab] = useState('capsules') // 'capsules' | 'responses'

  useEffect(() => {
    loadUserProfile()
  }, [username])

  const loadUserProfile = async () => {
    setLoading(true)
    setNotFound(false)
    try {
      const p = await getUserPublicProfile(username)
      if (!p) { setNotFound(true); return }
      setProfileData(p)

      const [caps, resps, counts] = await Promise.all([
        getUserPublicCapsules(p.id),
        getUserPublicResponses(p.id),
        getFollowCounts(p.id).catch(err => {
          console.error('Failed to load follow counts:', err)
          return { followers: 0, following: 0 }
        })
      ])
      setCapsules(caps)
      setResponses(resps)
      setFollowCounts(counts)
    } catch (e) {
      console.error('Failed to load user profile:', e.message)
      setNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const getInitials = (name) =>
    name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'

  const isOwnProfile = user && profileData && user.id === profileData.id
  const handleProfileFollowChange = (nextIsFollowing) => {
    setFollowCounts(prev => ({
      ...prev,
      followers: Math.max(0, (prev.followers || 0) + (nextIsFollowing ? 1 : -1))
    }))
  }

  return (
    <div className="userprofile-page">
      {/* Header */}
      <header className="feed-header">
        <div className="feed-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/feed')}>
          Agora
          <span className="feed-logo-beta">Beta</span>
        </div>

        <form className="feed-search-form" onSubmit={handleSearchSubmit}>
          <span className="feed-search-icon">⌕</span>
          <input
            ref={searchInputRef}
            className="feed-search-input"
            placeholder="Search topics, ideas, people…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="feed-search-clear"
              onClick={() => { setSearchQuery(''); searchInputRef.current?.focus() }}
            >×</button>
          )}
        </form>

        <div className="feed-header-right">
          <DirectMessagesButton />
          <NotificationButton userId={user?.id} />
          <button className="feed-profile-btn" onClick={() => navigate('/profile')}>
            <div
              className="feed-avatar"
              style={{ background: myProfile?.avatar_color || '#152550' }}
            >
              {myProfile?.avatar_url ? (
                <img src={myProfile.avatar_url} alt="" className="feed-avatar-image" />
              ) : (
                getInitials(myProfile?.display_name)
              )}
            </div>
            <span>{myProfile?.display_name || user?.email}</span>
          </button>
          <button className="feed-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </header>

      {/* Content */}
      <div className="userprofile-body">
        {/* Back button */}
        <button className="userprofile-back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>

        {loading && (
          <div className="userprofile-loading"><span className="spinner" /></div>
        )}

        {!loading && notFound && (
          <div className="userprofile-not-found">
            <div className="userprofile-not-found-glyph">◇</div>
            <div className="userprofile-not-found-title">User not found</div>
            <div className="userprofile-not-found-body">
              No user with the handle <strong>@{username}</strong> exists on Agora.
            </div>
          </div>
        )}

        {!loading && profileData && (
          <>
            {/* Profile card */}
            <div className="userprofile-card">
              <div className="userprofile-card-top">
                <div
                  className="userprofile-avatar"
                  style={{ background: profileData.avatar_color || '#152550' }}
                >
                  {profileData.avatar_url ? (
                    <img src={profileData.avatar_url} alt="" className="userprofile-avatar-image" />
                  ) : (
                    getInitials(profileData.display_name)
                  )}
                </div>
                <div className="userprofile-info">
                  <div className="userprofile-display-name">
                    {profileData.display_name || profileData.username}
                    {isOwnProfile && (
                      <span className="userprofile-you-badge">You</span>
                    )}
                  </div>
                  <div className="userprofile-username">@{profileData.username}</div>
                  <div className="userprofile-actions">
                    <FollowButton
                      currentUserId={user?.id}
                      targetUserId={profileData.id}
                      variant="profile"
                      onChange={handleProfileFollowChange}
                    />
                    {!isOwnProfile && (
                      <button
                        type="button"
                        className="userprofile-message-btn"
                        onClick={() => navigate(`/messages?user=${encodeURIComponent(profileData.username)}`)}
                      >
                        Message
                      </button>
                    )}
                  </div>
                  {profileData.bio && (
                    <div className="userprofile-bio">{profileData.bio}</div>
                  )}
                  {profileData.interests?.length > 0 && (
                    <div className="userprofile-interests">
                      {profileData.interests.map(i => (
                        <span key={i} className="userprofile-interest-tag">{i}</span>
                      ))}
                    </div>
                  )}

                  {/* Credibility fields */}
                  {(profileData.institution || profileData.field_of_study || profileData.external_website) && (
                    <div className="userprofile-credibility">
                      {profileData.institution && (
                        <div className="userprofile-credibility-item">
                          <span className="userprofile-credibility-icon">🏫</span>
                          <span className="userprofile-credibility-value">{profileData.institution}</span>
                        </div>
                      )}
                      {profileData.field_of_study && (
                        <div className="userprofile-credibility-item">
                          <span className="userprofile-credibility-icon">🎓</span>
                          <span className="userprofile-credibility-value">{profileData.field_of_study}</span>
                        </div>
                      )}
                      {profileData.external_website && (
                        <div className="userprofile-credibility-item">
                          <span className="userprofile-credibility-icon">🔗</span>
                          <a
                            href={profileData.external_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="userprofile-credibility-value userprofile-credibility-link"
                          >
                            {profileData.external_website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="userprofile-stats">
                  <div className="userprofile-stat">
                    <div className="userprofile-stat-val">{profileData.depth_score || 0}</div>
                    <div className="userprofile-stat-label">Depth</div>
                  </div>
                  <div className="userprofile-stat">
                    <div className="userprofile-stat-val">{profileData.capsule_count || 0}</div>
                    <div className="userprofile-stat-label">Capsules</div>
                  </div>
                  <div className="userprofile-stat">
                    <div className="userprofile-stat-val">{profileData.response_count || 0}</div>
                    <div className="userprofile-stat-label">Responses</div>
                  </div>
                  <div className="userprofile-stat">
                    <div className="userprofile-stat-val">{followCounts.followers || 0}</div>
                    <div className="userprofile-stat-label">Followers</div>
                  </div>
                  <div className="userprofile-stat">
                    <div className="userprofile-stat-val">{followCounts.following || 0}</div>
                    <div className="userprofile-stat-label">Following</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="userprofile-tabs">
              <button
                className={`userprofile-tab ${activeTab === 'capsules' ? 'active' : ''}`}
                onClick={() => setActiveTab('capsules')}
              >
                Capsules
                <span className="userprofile-tab-count">{capsules.length}</span>
              </button>
              <button
                className={`userprofile-tab ${activeTab === 'responses' ? 'active' : ''}`}
                onClick={() => setActiveTab('responses')}
              >
                Responses
                <span className="userprofile-tab-count">{responses.length}</span>
              </button>
            </div>

            {/* Capsules tab */}
            {activeTab === 'capsules' && (
              <>
                {capsules.length === 0 ? (
                  <div className="userprofile-empty">
                    <span className="userprofile-empty-glyph">◇</span>
                    <span>{profileData.display_name || profileData.username} hasn't published any capsules yet.</span>
                  </div>
                ) : (
                  <div className="userprofile-capsules">
                    {capsules.map(c => (
                      <CapsuleCard key={c.id} capsule={c} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Responses tab */}
            {activeTab === 'responses' && (
              <>
                {responses.length === 0 ? (
                  <div className="userprofile-empty">
                    <span className="userprofile-empty-glyph">◇</span>
                    <span>{profileData.display_name || profileData.username} hasn't published any responses yet.</span>
                  </div>
                ) : (
                  <div className="userprofile-responses">
                    {responses.map(r => (
                      <ResponseItem key={r.id} response={r} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Lightweight card for a response on a user's public profile
function ResponseItem({ response }) {
  const capsule = response.capsules
  const snippet = capsule?.content?.slice(0, 80) + (capsule?.content?.length > 80 ? '…' : '')

  return (
    <div className="userprofile-response-item">
      {snippet && (
        <div className="userprofile-response-capsule-snippet">
          ↳ responding to: <em>{snippet}</em>
        </div>
      )}
      <div className="userprofile-response-body">{response.content}</div>
      <div className="userprofile-response-meta">
        {new Date(response.publishes_at).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric'
        })}
        {response.like_count > 0 && (
          <span className="userprofile-response-likes">♥ {response.like_count}</span>
        )}
      </div>
    </div>
  )
}
